import { Link, useSearchParams } from "react-router-dom";
import styles from "./Confirmation.module.css";
import { useEffect, useState } from "react";
import { getCart, clearCart } from "../../utils/cartUtils";
import { API_BASE } from "../../config/api";

interface Product {
  id: number;
  name: string;
  price: number;
}

interface User {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  street?: string;
  houseNumber?: string;
  postalCode?: string;
  city?: string;
  country?: string;
  phone?: string;
}

interface Order {
  id: number;
  products: Product[];
  orderDate: string;
  totalAmount: number;
  orderNumber: string;
}



// Verbesserte fetchWithAuth Funktion
async function fetchWithAuth(path: string, init: RequestInit = {}) {
  const finalInit: RequestInit = {
    ...init,
    credentials: 'include',
    headers: {
      "Content-Type": "application/json",
      ...(init.headers || {})
    }
  };

  try {
    let res = await fetch(API_BASE + path, finalInit);

    if (res.status === 401) {
      console.log("Token expired, trying refresh...");
      const refresh = await fetch(API_BASE + "/api/auth/refresh", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" }
      });

      if (refresh.ok) {
        console.log("Refresh successful, retrying request...");
        res = await fetch(API_BASE + path, finalInit);
      } else {
        console.log("Refresh failed, clearing user data");
        // User data clear logic here if needed
      }
    }

    return res;
  } catch (error) {
    console.error("Fetch error:", error);
    throw error;
  }
}

// Separate Funktion für öffentliche Requests (ohne Auth)
async function fetchPublic(path: string) {
  const res = await fetch(API_BASE + path);
  if (!res.ok) {
    throw new Error(`HTTP error! status: ${res.status}`);
  }
  return res.json();
}

export default function Confirmation() {
  const [user, setUser] = useState<User | null>(null);
  const [cartProducts, setCartProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [orderError, setOrderError] = useState<string | null>(null);
  const [createdOrder, setCreatedOrder] = useState<Order | null>(null);
  const [searchParams] = useSearchParams();
  const paymentSuccess = searchParams.get('success') === 'true';
  const sessionId = searchParams.get('session_id');
//console.log('Payment success:', paymentSuccess);
//console.log('Stripe session ID:', sessionId);

  // Load user and cart data
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        // 1. User laden
      //  console.log("Lade User...");
        const meRes = await fetchWithAuth("/api/auth/me");
        if (meRes.ok) {
          const userData = await meRes.json();
          setUser(userData);
         // console.log("User geladen:", userData);
        } else {
         // console.warn("User konnte nicht geladen werden:", meRes.status);
        }

        // 2. Cart aus localStorage laden
        const cartIds = getCart();
        //console.log("Cart IDs:", cartIds);

        if (cartIds.length > 0) {
          // Versuche zuerst öffentlichen Endpoint, falls verfügbar
          try {
           // console.log("Lade Produkte...");
            // Verwende fetchWithAuth für protected route oder public falls verfügbar
            const productsRes = await fetchWithAuth("/api/auth/products");
            
            if (productsRes.ok) {
              const allProducts: Product[] = await productsRes.json();
              const fullCart = allProducts.filter((p) => cartIds.includes(p.id));
              setCartProducts(fullCart);
            //  console.log("Produkte geladen:", fullCart.length);
            } else {
             // console.warn("Produkte konnten nicht geladen werden:", productsRes.status);
              // Fallback: Verwende Cart IDs direkt ohne Produktdetails
              const fallbackProducts: Product[] = cartIds.map(id => ({
                id: id,
                name: `Produkt ${id}`,
                price: 0
              }));
              setCartProducts(fallbackProducts);
            }
          } catch (error) {
           // console.error("Fehler beim Laden der Produkte:", error);
            // Fallback
            const fallbackProducts: Product[] = cartIds.map(id => ({
              id: id,
              name: `Produkt ${id}`,
              price: 0
            }));
            setCartProducts(fallbackProducts);
          }
        } else {
         // console.log("Cart ist leer, versuche letzte Bestellung zu laden...");
          try {
            const lastOrderRes = await fetchWithAuth("/api/orders/latest");
            if (lastOrderRes.ok) {
              const lastOrder: Order = await lastOrderRes.json();
              setCartProducts(lastOrder.products);
              setCreatedOrder(lastOrder);
              setOrderSuccess(true);
           //   console.log("Letzte Bestellung geladen:", lastOrder);
            }
          } catch (err) {
          //  console.log("Keine letzte Bestellung gefunden");
          }
        }

      } catch (err) {
      //  console.error("Fehler beim Laden der Daten:", err);
        setOrderError("Error loading order data");
      } finally {
        setLoading(false);
      }
    };

    if (paymentSuccess) {
      loadData();
    } else {
      setLoading(false);
    }
  }, [paymentSuccess]);

   // Create order when we have user and cart data
  useEffect(() => {
    const createOrder = async () => {
      if (!paymentSuccess || !user || cartProducts.length === 0 || orderSuccess || createdOrder) {
        console.log("", {
          paymentSuccess,
          user: !!user,
          cartProducts: cartProducts.length,
          orderSuccess,
          createdOrder: !!createdOrder
        });
        return;
      }

      try {
      //  console.log("Starte Bestellungs-Erstellung...");
        const cartIds = cartProducts.map(p => p.id);
        
        const response = await fetchWithAuth( `/api/orders/create?userId=${user.id}&sessionId=${sessionId}`, {
          method: "POST",
          body: JSON.stringify(cartIds)
        });

       // console.log("Order API Response Status:", response.status);

        if (response.ok) {
          const order: Order = await response.json();
         // console.log("Bestellung erfolgreich erstellt:", order);
          setCreatedOrder(order);
          setOrderSuccess(true);
          setOrderError(null);
          
          // Cart leeren nach erfolgreicher Bestellung
          clearCart();
         // console.log("Warenkorb geleert");
        } else {
          let errorText = "The order could not be created.";
          try {
            const errorData = await response.json();
            errorText = errorData.error || errorData.message || errorText;
          } catch (e) {
            errorText = await response.text().catch(() => errorText);
          }
        //  console.error("Order API Error:", errorText);
          throw new Error(errorText);
        }
      } catch (err) {
       // console.error("Fehler beim Erstellen der Bestellung:", err);
        setOrderError(err instanceof Error ? err.message : "Unknown error during order creation");
        setOrderSuccess(false);
      }
    };

    createOrder();
  }, [user, cartProducts, paymentSuccess, orderSuccess, createdOrder]);

  // Show loading state
  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.loading}>
            <h2>Order is being processed...</h2>
            <p>Please wait while we review your order.</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error if no payment success
  if (!paymentSuccess) {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <h1 className={styles.title}>Payment not completed</h1>
          <div className={styles.errorIcon}>⚠️</div>
          <p className={styles.errorText}>
           The payment was not successfully completed or was cancelled.
          </p>
          <div className={styles.actions}>
            
            <Link to="/" className={styles.continueShopping}>
             Back to homepage
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const total = cartProducts.reduce((sum, p) => sum + (p.price ?? 0), 0);

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        {orderSuccess && createdOrder ? (
          <>
            <h1 className={styles.title}>Order successful!</h1>
            
            <div className={styles.successIcon}>✓</div>
            
            <p className={styles.successText}>
             Thank you! Your order <strong>#{createdOrder.orderNumber}</strong> has been successfully completed.
            A confirmation and invoice have been sent to {user?.email}.
            </p>

      
          </>
        ) : orderError ? (
          <>
            <h1 className={styles.title}>Technical problem</h1>
            <div className={styles.errorIcon}>⚠️</div>
            <p className={styles.errorText}>
              {orderError}
            </p>
            <p className={styles.helpText}>
           Your payment was successful, but there was a problem with the order confirmation.
Please contact our support team using your email address.
            </p>
          </>
        ) : (
          <div className={styles.loading}>
            <h2>Order is being prepared......</h2>
            <p>Please wait, we are completing your order.</p>
          </div>
        )}

        <div className={styles.actions}>
          <Link to="/" className={styles.homeButton}>
            Back to home page
          </Link>
        </div>
      </div>
    </div>
  );
}